import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, of, switchMap, tap, timer } from 'rxjs';

import { Injectable, inject } from '@angular/core';
import { CareManagementApiService, VoiceCallContext } from '@troyai/care-management/data-access';
import { LoadingState, getLoadingStatus } from '@troyai/shared/state';

import { VoiceCallsApiService } from '../voice-calls-api.service';
import { AircallStateModel } from './aircall-state.model';
import {
  AircallCallEndRingtone,
  AircallIncomingCall,
  AircallOutgoingCallAnswered,
  AircallOutgoingCallStarted,
  AircallTogglePanel,
  AircallUserLoggedIn,
  AircallUserLoggedOut,
  CancelDial,
  ClearCallState,
  DialPhoneNumber,
  FinalizePostCallWrapUp,
  GetAllMembersOutreachHistory,
  SetVoiceCallContext,
  TriggerPostCallWrapUp,
  TriggerPreCallPrep,
} from './aircall.actions';

@Injectable()
export class MemberCareStateService {}

@State<AircallStateModel>({
  name: 'aircallState',
  defaults: {
    user: null,
    callIntentContext: null,
    currentCall: null,
    callPhase: 'idle',
    isLoggedIn: false,
    isPanelOpen: false,
    memberOutreachHistory: {
      result: null,
      loadingStatus: LoadingState.INITIAL,
    },
  },
})
@Injectable()
export class AircallState {
  private voiceCallsApiService = inject(VoiceCallsApiService);
  private careManagementApiService = inject(CareManagementApiService);

  @Selector()
  static isPanelOpen(state: AircallStateModel) {
    return state.isPanelOpen;
  }

  @Selector()
  static isLoggedIn(state: AircallStateModel) {
    return state.isLoggedIn;
  }

  @Selector()
  static currentUser(state: AircallStateModel) {
    return state.user;
  }

  @Selector()
  static hasOngoingCall(state: AircallStateModel) {
    return state.callPhase === 'ringing' || state.callPhase === 'on-call';
  }

  @Selector()
  static isPreparing(state: AircallStateModel) {
    return state.callPhase === 'preparing';
  }

  @Selector()
  static currentCall(state: AircallStateModel) {
    return state.currentCall;
  }

  @Selector()
  static memberOutreachHistory(state: AircallStateModel) {
    return state.memberOutreachHistory.result;
  }

  @Selector()
  static memberOutreachHistoryLoadingStatus(state: AircallStateModel) {
    return getLoadingStatus(state.memberOutreachHistory.loadingStatus);
  }

  @Selector()
  static callIntentContext(state: AircallStateModel) {
    return state.callIntentContext;
  }

  @Selector()
  static callPhase(state: AircallStateModel) {
    return state.callPhase;
  }

  @Action(AircallUserLoggedIn)
  userLoggedIn(ctx: StateContext<AircallStateModel>, action: AircallUserLoggedIn) {
    ctx.patchState({
      user: action.payload,
      isLoggedIn: true,
    });
  }

  @Action(AircallUserLoggedOut)
  userLoggedOut(ctx: StateContext<AircallStateModel>) {
    ctx.patchState({
      user: null,
      isLoggedIn: false,
    });
  }

  @Action(AircallIncomingCall)
  incomingCall(ctx: StateContext<AircallStateModel>, action: AircallIncomingCall) {
    const { callPhase } = ctx.getState();

    // Only accept incoming calls when truly idle
    // Ignore if preparing (user initiated outbound dial), ringing, on-call, or wrapping-up
    if (callPhase !== 'idle') {
      return;
    }

    return ctx.patchState({
      callPhase: 'ringing',
      currentCall: {
        id: action.payload.call.call_id.toString(),
        from: action.payload.call.from,
        to: action.payload.call.to,
        answered: false,
        direction: 'inbound',
        ended: false,
      },
    });
  }

  @Action(AircallCallEndRingtone)
  callEndRingtone(ctx: StateContext<AircallStateModel>, action: AircallCallEndRingtone) {
    const callPhase = ctx.getState().callPhase;

    // Only process if we're in ringing (accepted call) or on-call phase.
    // Reject if idle (no call tracked) or wrapping-up (previous call still finalizing).
    if (callPhase !== 'ringing' && callPhase !== 'on-call') {
      return;
    }

    const airCallId = action.payload.call.call_id.toString();
    const existingContext = ctx.getState().callIntentContext;
    const isOutboundWithContext = existingContext?.memberGlobalId;

    // For inbound calls (no pre-existing context), create a blank call context.
    // For outbound calls with member context, skip — outgoingCallStarted handles it.
    const createContext$ = isOutboundWithContext
      ? of(null)
      : timer(100).pipe(
          switchMap(() =>
            this.voiceCallsApiService.createCallContext(airCallId, {
              provider_call_id: airCallId,
              member_global_id: 0,
              outreach_purposes: [],
            })
          ),
          tap((response) => {
            ctx.patchState({
              callIntentContext: {
                id: response.provider_call_id,
              },
            });
          }),
          switchMap(() => of(null))
        );

    return createContext$.pipe(
      tap(() => {
        const currentCall = ctx.getState().currentCall;
        if (!currentCall) {
          return;
        }

        const isAnswered = action.payload.call.answer_status === 'answered';
        const isDisconnected =
          action.payload.call.answer_status === 'disconnected' ||
          action.payload.call.answer_status === 'refused';

        ctx.patchState({
          callPhase: isAnswered
            ? 'on-call'
            : isDisconnected
              ? callPhase === 'on-call'
                ? 'wrapping-up'
                : 'idle'
              : 'ringing',
          currentCall: {
            ...currentCall,
            id: airCallId,
            answered: isAnswered,
            ended: isDisconnected,
          },
        });

        if (isAnswered) {
          ctx.dispatch(
            new TriggerPreCallPrep({
              phoneNumber: currentCall.from,
            })
          );
        }

        if (isDisconnected) {
          if (callPhase === 'on-call') {
            ctx.dispatch(new TriggerPostCallWrapUp());
          } else {
            ctx.dispatch(new ClearCallState());
          }
        }
      }),
      catchError(() => {
        // Swallow error after retries and complete the stream.
        return of(null);
      })
    );
  }

  // @Action(AircallCallEnded)
  // callEnded(ctx: StateContext<AircallStateModel>) {

  // const { currentCall, callPhase } = ctx.getState();

  // if (!currentCall) {
  //   return;
  // }

  // // Already wrapping up (e.g. disconnected/refused in callEndRingtone) — don't re-trigger.
  // if (callPhase === 'wrapping-up' || callPhase === 'idle') {
  //   return;
  // }

  // ctx.patchState({
  //   callPhase: 'wrapping-up',
  //   currentCall: {
  //     ...currentCall,
  //     ended: true,
  //   },
  // });

  // return ctx.dispatch(new TriggerPostCallWrapUp());
  // }

  @Action(AircallTogglePanel)
  togglePanel(ctx: StateContext<AircallStateModel>) {
    const state = ctx.getState();

    return ctx.patchState({
      isPanelOpen: !state.isPanelOpen,
    });
  }

  @Action(AircallOutgoingCallStarted)
  outgoingCallStarted(ctx: StateContext<AircallStateModel>, action: AircallOutgoingCallStarted) {
    const { callPhase } = ctx.getState();

    // Allow outgoing calls from idle (direct dial) or preparing (user clicked dial button)
    if (callPhase !== 'idle' && callPhase !== 'preparing') {
      return;
    }

    const callIntentMetadata = ctx.getState().callIntentContext;
    const airCallId = action.payload.call.call_id.toString();

    // Always track the call in state, regardless of member context
    ctx.patchState({
      callPhase: 'ringing',
      currentCall: {
        id: airCallId,
        from: action.payload.call.from,
        to: action.payload.call.to,
        answered: false,
        direction: 'outbound',
        ended: false,
      },
    });

    if (!callIntentMetadata || !callIntentMetadata.memberGlobalId) {
      return;
    }

    ctx.patchState({
      memberOutreachHistory: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });

    return this.careManagementApiService
      .getMemberOutreachHistory(callIntentMetadata.memberGlobalId, 'ongoing-cycle', 'general-cm')
      .pipe(
        tap((historyItems) => {
          ctx.patchState({
            memberOutreachHistory: {
              result: historyItems,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        switchMap(() => {
          return timer(100).pipe(
            switchMap(() =>
              this.voiceCallsApiService.createCallContext(airCallId, {
                provider_call_id: airCallId,
                member_global_id: callIntentMetadata.memberGlobalId || 0,
                outreach_purposes: callIntentMetadata.purpose || [],
              })
            ),
            tap((response) => {
              const existingCallIntentContext = ctx.getState().callIntentContext;

              if (!existingCallIntentContext) {
                return;
              }

              ctx.patchState({
                callIntentContext: {
                  ...existingCallIntentContext,
                  id: response.provider_call_id,
                },
              });
            })
          );
        })
      );
  }

  @Action(AircallOutgoingCallAnswered)
  outgoingCallAnswered(ctx: StateContext<AircallStateModel>) {
    const currentCall = ctx.getState().currentCall;

    if (!currentCall) {
      return;
    }

    return ctx.patchState({
      callPhase: 'on-call',
      currentCall: {
        ...currentCall,
        answered: true,
      },
    });
  }

  @Action(DialPhoneNumber)
  dialPhoneNumber(ctx: StateContext<AircallStateModel>, action: DialPhoneNumber) {
    return ctx.patchState({
      isPanelOpen: true,
    });
  }

  @Action(CancelDial)
  cancelDial(ctx: StateContext<AircallStateModel>) {
    const { callPhase } = ctx.getState();

    // Only allow canceling from preparing phase
    if (callPhase !== 'preparing') {
      return;
    }

    return ctx.patchState({
      callPhase: 'idle',
      callIntentContext: null,
      isPanelOpen: false,
    });
  }

  @Action(SetVoiceCallContext)
  setVoiceCallContext(ctx: StateContext<AircallStateModel>, action: SetVoiceCallContext) {
    const callIntentContext = ctx.getState().callIntentContext;
    const newIntentContext = {
      ...callIntentContext,
      ...action.payload.contextData,
    } as VoiceCallContext;

    const currentCall = ctx.getState().currentCall;

    if (
      action.payload.persist &&
      newIntentContext.memberGlobalId &&
      currentCall &&
      callIntentContext?.id
    ) {
      return this.voiceCallsApiService
        .updateCallContext(callIntentContext.id, {
          provider_call_id: currentCall.id,
          post_call_actions: newIntentContext.postCallActions,
          member_global_id: newIntentContext.memberGlobalId,
          outreach_purposes: newIntentContext.purpose || [],
          activities_completed: newIntentContext.activitiesCompleted || [],
          outreach_outcome: newIntentContext.outcome || '',
        })
        .pipe(
          tap(() => {
            ctx.patchState({
              callIntentContext: newIntentContext,
            });
          })
        );
    }

    return ctx.patchState({
      callIntentContext: newIntentContext,
    });
  }

  @Action(ClearCallState)
  clearCallState(ctx: StateContext<AircallStateModel>) {
    return ctx.patchState({
      callPhase: 'idle',
      callIntentContext: null,
      currentCall: null,
      isPanelOpen: false,
      memberOutreachHistory: {
        result: null,
        loadingStatus: LoadingState.INITIAL,
      },
    });
  }

  @Action(FinalizePostCallWrapUp)
  finalizePostCallWrapUp(ctx: StateContext<AircallStateModel>) {
    const callIntentContext = ctx.getState().callIntentContext;

    if (!callIntentContext) {
      return ctx.dispatch(new ClearCallState());
    }

    if (!callIntentContext.id) {
      return ctx.dispatch(new ClearCallState());
    }

    return this.voiceCallsApiService
      .updateCallContext(callIntentContext.id, {
        provider_call_id: ctx.getState().currentCall?.id || '',
        post_call_actions: callIntentContext.postCallActions,
        member_global_id: callIntentContext.memberGlobalId || 0,
        outreach_purposes: callIntentContext.purpose || [],
        outreach_outcome: callIntentContext.outcome || '',
        activities_completed: callIntentContext.activitiesCompleted || [],
      })
      .pipe(tap(() => ctx.dispatch(new ClearCallState())));
  }

  @Action(GetAllMembersOutreachHistory)
  getAllMembersOutreachHistory(
    ctx: StateContext<AircallStateModel>,
    action: GetAllMembersOutreachHistory
  ) {
    ctx.patchState({
      memberOutreachHistory: {
        result: null,
        loadingStatus: LoadingState.LOADING,
      },
    });
    return this.careManagementApiService
      .getMemberOutreachHistory(action.payload.memberGlobalId, action.payload.filter)
      .pipe(
        tap((response) => {
          ctx.patchState({
            memberOutreachHistory: {
              result: response,
              loadingStatus: LoadingState.LOADED,
            },
          });
        }),
        catchError(() => {
          ctx.patchState({
            memberOutreachHistory: {
              result: null,
              loadingStatus: LoadingState.ERRORED,
            },
          });
          return of(null);
        })
      );
  }
}
